import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUrlState } from "@/hooks/useUrlState";
import { isFilterablePage } from "@/lib/pageFilter";
import { cn } from "@/lib/utils";
import { useBindings } from "@/hooks/useShortcuts";
import { formatCombo, matchesTypeable } from "@/lib/shortcuts";

/**
 * The page filter box: top-right of every page (PageHead) and every modal
 * (Modal), focused with `/`.
 *
 * Two modes, chosen by where it sits:
 * - On a filterable list page it IS the page's filter: it writes the `?q=`
 *   param the page applies (debounced — a controlled input round-tripping
 *   through the router eats fast keystrokes), and leaving the page drops it.
 * - Everywhere else it highlights matches in place rather than filtering:
 *   matching text in the scope (the page's main column, or the modal's body)
 *   is marked, with a count. Nothing is hidden — on a page that is not a list,
 *   hiding rows would be a filter with no contract behind it.
 */
const CURRENT_CLASS = "page-filter-hit-current";

export function PageFilterBox({ scopeRef }: { scopeRef?: React.RefObject<HTMLElement | null> }) {
  const { pathname } = useLocation();
  const filterable = isFilterablePage(pathname) && !scopeRef; // a modal always highlights
  // Page-level boxes grab focus on page load; a modal's box never does, so it
  // can't fight the modal's own first field.
  const autoFocus = !scopeRef;
  return filterable ? <FilterMode autoFocus={autoFocus} /> : <HighlightMode scopeRef={scopeRef} autoFocus={autoFocus} />;
}

function FilterMode({ autoFocus }: { autoFocus?: boolean }) {
  const [pageQ, setPageQ] = useUrlState("q");
  const [box, setBox] = useState(pageQ);
  const lastWritten = useRef(pageQ);
  useEffect(() => {
    if (pageQ !== lastWritten.current) setBox(pageQ);
    lastWritten.current = pageQ;
  }, [pageQ]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (box !== pageQ) {
        lastWritten.current = box;
        setPageQ(box);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [box, pageQ, setPageQ]);
  // Arrows and Enter belong to the row cursor (useRowNav), which reads them off
  // the window while this box keeps focus; the box only handles Escape.
  return <Box value={box} onChange={setBox} hint="filter rows" autoFocus={autoFocus} />;
}

function HighlightMode({ scopeRef, autoFocus }: { scopeRef?: React.RefObject<HTMLElement | null>; autoFocus?: boolean }) {
  const bindings = useBindings();
  const [box, setBox] = useState("");
  const [count, setCount] = useState(0);
  const [debounced, setDebounced] = useState("");
  // Which match the cursor sits on, -1 before the first Arrow/Enter. Marks are
  // re-queried per step rather than held, so a re-render that rebuilds them
  // can't leave the cursor pointing at a detached node.
  const [current, setCurrent] = useState(-1);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(box), 200);
    return () => clearTimeout(t);
  }, [box]);
  useEffect(() => setCurrent(-1), [debounced]);

  useEffect(() => {
    const root = scopeRef?.current ?? document.getElementById("main-content");
    if (!root) return;
    // Clear previous marks first: unwrap every mark and re-normalize, so a
    // stale highlight never survives the term changing.
    const stale = root.querySelectorAll("mark.page-filter-hit");
    stale.forEach((m) => m.replaceWith(document.createTextNode(m.textContent ?? "")));
    // normalize() merges adjacent text nodes that React owns separately (`{a}/{b}`),
    // so a later React update throws removeChild NotFoundError. Only run it when
    // marks were actually unwrapped.
    if (stale.length) root.normalize();
    const term = debounced.trim().toLowerCase();
    if (!term) { setCount(0); return; }
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) =>
        n.parentElement?.closest("script,style,input,textarea,select,mark,[aria-hidden='true']")
          ? NodeFilter.FILTER_REJECT
          : n.nodeValue?.toLowerCase().includes(term)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_SKIP,
    });
    const nodes: Text[] = [];
    while (walker.nextNode()) nodes.push(walker.currentNode as Text);
    let hits = 0;
    for (const node of nodes) {
      const text = node.nodeValue ?? "";
      const frag = document.createDocumentFragment();
      let at = 0;
      let idx = text.toLowerCase().indexOf(term, at);
      while (idx !== -1) {
        frag.append(document.createTextNode(text.slice(at, idx)));
        const mark = document.createElement("mark");
        mark.className = "page-filter-hit";
        mark.textContent = text.slice(idx, idx + term.length);
        frag.append(mark);
        hits++;
        at = idx + term.length;
        idx = text.toLowerCase().indexOf(term, at);
      }
      frag.append(document.createTextNode(text.slice(at)));
      node.replaceWith(frag);
    }
    setCount(hits);
    return () => {
      const marked = root.querySelectorAll("mark.page-filter-hit");
      marked.forEach((m) => m.replaceWith(document.createTextNode(m.textContent ?? "")));
      if (marked.length) root.normalize();
    };
  }, [debounced, scopeRef]);

  const marks = (): HTMLElement[] => {
    const root = scopeRef?.current ?? document.getElementById("main-content");
    return root ? [...root.querySelectorAll<HTMLElement>("mark.page-filter-hit")] : [];
  };

  useEffect(() => {
    const all = marks();
    // The count is re-read from the DOM every render rather than trusted from
    // the pass that wrote the marks: a page re-render can take marks away with
    // the term unchanged, and a hint reading "2/28" over 27 matches is a number
    // the reader can't act on. Same for a cursor left past the end.
    if (all.length !== count) setCount(all.length);
    if (current >= all.length) { setCurrent(all.length - 1); return; }
    all.forEach((m, i) => m.classList.toggle(CURRENT_CLASS, i === current));
    if (current >= 0) all[current]?.scrollIntoView({ block: "center" });
  });

  const step = (delta: number) => {
    const all = marks();
    if (all.length === 0) return;
    setCurrent((c) => (c < 0 ? (delta > 0 ? 0 : all.length - 1) : (c + delta + all.length) % all.length));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Only combos that aren't a bare character: this box has to stay typeable.
    if (matchesTypeable(e, bindings.moveDown)) { e.preventDefault(); step(1); }
    else if (matchesTypeable(e, bindings.moveUp)) { e.preventDefault(); step(-1); }
    else if (matchesTypeable(e, bindings.open)) {
      e.preventDefault();
      // Enter opens what the match sits in — a link, a row, a disclosure —
      // and only walks to the next match when it sits in nothing clickable.
      const target = marks()[current]?.closest<HTMLElement>('a[href], button, [role="button"], [role="link"], [aria-expanded]');
      if (current >= 0 && target) target.click();
      else step(1);
    }
  };

  const hint = debounced.trim()
    ? current >= 0 && count > 0
      ? `${current + 1}/${count}`
      : `${count} match${count === 1 ? "" : "es"}`
    : "highlight";
  return <Box value={box} onChange={setBox} onKeyDown={onKeyDown} hint={hint} autoFocus={autoFocus} />;
}

function Box({ value, onChange, onKeyDown, hint, autoFocus }: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  hint: string;
  autoFocus?: boolean;
}) {
  const bindings = useBindings();
  const inputRef = useRef<HTMLInputElement>(null);
  // Focus once when this box mounts (i.e. when the page loads), not on every
  // re-render — typing into another field afterwards must not lose focus.
  const focusedOnce = useRef(false);
  useEffect(() => {
    if (autoFocus && !focusedOnce.current) {
      focusedOnce.current = true;
      inputRef.current?.focus();
    }
  }, [autoFocus]);
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-border bg-secondary px-2">
      <input
        ref={inputRef}
        aria-label="Filter this page"
        title={`Filter or highlight this page (${formatCombo(bindings.filterPage[0] ?? "/")})`}
        placeholder="Filter"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Escape empties the box first and only then gives focus back to the
          // page, so one key never both clears and leaves.
          if (matchesTypeable(e, bindings.dismiss)) {
            e.preventDefault();
            if (value) onChange("");
            else inputRef.current?.blur();
            return;
          }
          onKeyDown?.(e);
        }}
        className="w-24 sm:w-32 bg-transparent py-1.5 text-[13px] outline-none"
      />
      <span className={cn("text-[10px] text-muted-foreground whitespace-nowrap")}>{hint}</span>
      <kbd className="text-[10px] text-muted-foreground">{formatCombo(bindings.filterPage[0] ?? "/")}</kbd>
    </div>
  );
}
